import { html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import componentStyles from './search-hero.css.lit.ts';
import QueryComponent from '../../base/query-component/QueryComponent.js';
import { debounce } from '../../utils/debounce';

export default class SearchHero extends QueryComponent {
  static styles = [super.styles, componentStyles];

  static get properties() {
    return {
      breadCrumbLinks: { type: Object },
      breadcrumbTitle: { type: String },
      header: { type: String },
      searchTextMobile: { type: String },
      placeholder: { type: String },
      buttonText: { type: String },
      listLink: { type: String },
      listLinkText: { type: String },
      searchUrl: { type: String },
      contenttype: { type: String },
    };
  }

  constructor() {
    super();
    const urlParams = new URLSearchParams(window.location.search);
    this.minSearchChars = 4;
    this.searchValue = urlParams.get('search') || '';
    this.showClearButton = this.searchValue.length > 0;
    this.searchUrl = '/search?search=';
    this.panelOpen = false;
    this.fullSearch = {
      items: [],
      viewMoreUrl: '#',
    };
  }

  _handleClear() {
    this.searchValue = '';
    this.showClearButton = false;
    this.requestUpdate();
  }

  _handleChangeDebounced(e) {
    debounce(this._handleChange(e));
  }

  _handleChange(e) {
    this.showClearButton = e.target.value.length > 0;
    this.searchValue = e.target.value;
    e.preventDefault();

    // if (
    //   this.searchUrl.length &&
    //   this.searchValue.length >= this.minSearchChars
    // ) {
    //   this.openPanel();
    //   this._handleSearch();
    // } else {
    //   this.closePanel();
    // }

    // Enter key
    if (e.keyCode === 13) {
      this._handleSubmit();
    }

    // Escape key
    if (e.keyCode === 27) {
      this.closePanel();
    }

    this.requestUpdate();
  }

  connectedCallback() {
    super.connectedCallback();
    this.closePanelHandler = this.closePanel.bind(this);
    window.addEventListener(
      'hf-search-hero-close-panel',
      this.closePanelHandler
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.closePanelHandler = this.closePanel.bind(this);
    window.removeEventListener(
      'hf-search-hero-close-panel',
      this.closePanelHandler
    );
  }

  closePanel() {
    document.querySelector('body').classList.remove('search-panel-open');
    this.panelOpen = false;
    this.requestUpdate();
  }

  openPanel() {
    document.querySelector('body').classList.add('search-panel-open');
    this.panelOpen = true;
  }

  _handleData(data) {
    if (this.contenttype === 'departments_services') {
      this.fullSearch = {
        items: [...data.departments_and_services, ...data.physicians],
        viewMoreUrl: '#',
      };
    } else if (this.contenttype === 'location') {
      this.fullSearch = {
        items: data.locations_search,
        viewMoreUrl: '#',
      };
    } else {
      this.fullSearch = {
        items: data.full_search,
        viewMoreUrl: '#',
      };
    }

    this.requestUpdate();
  }

  _handleSubmit() {
    let url = this.searchUrl + this.searchValue;
    if (this.contenttype) {
      url += '&f[0]=content_type:' + this.contenttype;
    }
    window.location = url;
  }

  // @TODO: refactor as something that we can import into other components
  doQuery(url) {
    // cancel pending request if any
    if (this.abortController) this.abortController.abort();
    // make our request cancellable
    this.abortController = new AbortController();
    const signal = this.abortController.signal;
    return (
      fetch(url, { method: 'post', signal })
        // clean up, not really needed but may help to know if there is a pending request
        .then(r => {
          this.abortController = null;
          return r;
        })
    );
  }

  _handleSearch() {
    const searchValue = this.searchValue.trim();
    let url = '/search.ajax/' + searchValue + '?search=' + searchValue;
    if (this.contenttype) {
      url += '&f[0]=content_type:' + this.contenttype;
    }

    this.doQuery(url)
      .then(response => response.json())
      .then(data => this._handleData(data))
      .catch(() => {});
  }

  _focusIn() {
    // if (
    //   this.searchUrl.length &&
    //   this.searchValue.length >= this.minSearchChars
    // ) {
    //   this.openPanel();
    // }
    this.requestUpdate();
  }

  _focusOut(e) {
    if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget)) {
      this.panelOpen = false;
      this.requestUpdate();
    }
  }

  _handleOverlay() {
    this.panelOpen = false;
    this.requestUpdate();
  }

  firstUpdated() {
    super.firstUpdated();
    this.abortController = new AbortController();
    if (this.header === 'Departments & Services') {
      this.contenttype = 'departments_services';
    }

    if (this.searchValue.length > this.minSearchChars) {
      this._handleSearch();
    }
  }

  renderOffleft(array) {
    const matchTxt = array.length <= 1 ? 'match found' : 'matches found';
    return html`<span class="search-hero__offleft" tabindex="-1">
      ${array.length} ${matchTxt}
    </span>`;
  }

  render() {
    return html`
      <hf-page-header class="search-hero">
        <div slot="breadcrumb">
          <slot name="breadcrumb-children"></slot>
        </div>
        <div slot="header-main">
          <hf-heading
            class="search-hero__header"
            level="1"
            levelStyle="heading1"
            >${this.header}</hf-heading
          >
          <div class="search-hero__help-text">
            ${Drupal.t(
              'Search by department, service, procedure, condition or treatment'
            )}
          </div>
          <div class="search-hero__input" @focusout=${this._focusOut}>
            <div class="search-hero__input-wrapper">
              <label
                for="searchHero"
                id="search-hero__description"
                class="search-hero__offleft"
                >Search by department, service, procedure, condition or
                treatment</label
              >
              <input
                name="searchHero"
                class="search-hero__input-textfield"
                id="searchHero"
                type="text"
                aria-describedby="search-hero__description"
                aria-owns="search__panel__results"
                aria-haspopup="listbox"
                aria-expanded="${this.panelOpen}"
                role="combobox"
                placeholder=${this._mediaQuery('md', 'min')
                  ? this.placeholder
                  : ''}
                title=${this.placeholder}
                aria-label=${this.placeholder}
                .value=${this.searchValue}
                @keyup="${this._handleChangeDebounced}"
                @focus="${this._focusIn}"
              />
              ${this.renderOffleft(this.fullSearch.items)}
              <button
                @click="${this._handleClear}"
                aria-label="Close"
                class="${classMap({
                  'search-hero__input-clear': true,
                  'search-hero__input-clear--show': this.showClearButton,
                })}"
                aria-label="Clear Search Field"
              >
                <hf-icon name="close" color="purple"></hf-icon>
              </button>
            </div>
            <hf-button
              type="primary-white-search"
              icon="search"
              @click="${this._handleSubmit}"
              class="search-hero__input-submit"
            >
              ${this.buttonText}
            </hf-button>
            <div
              @click="${this._handleOverlay}"
              class="${classMap({
                'search-hero__overlay': true,
                'search-hero__overlay--show':
                  this.searchValue.length >= this.minSearchChars &&
                  this.panelOpen,
              })}"
            ></div>
            <hf-search-panel
              contentType="${this.contenttype}"
              suggestedOnly="${true}"
              .panelIsOpen="${this.panelOpen}"
              .searchValue="${this.searchValue}"
              .fullSearch="${this.fullSearch}"
            ></hf-search-panel>
          </div>

          ${this.contenttype === 'departments_services'
            ? nothing
            : html`
                <hf-takeover-modal styleclass="filters">
                  <div slot="button_content">
                    <div class="search-hero__mobile-filters">
                      <hf-icon
                        name="filter"
                        class="search-hero__mobile-filters-icon"
                      ></hf-icon>
                      Filter
                    </div>
                  </div>
                  <div slot="modal_content">
                    <div class="search-hero__modal-title">
                      <hf-icon
                        name="filter"
                        class="search-hero__modal-title-icon"
                      ></hf-icon>
                      Filters
                    </div>
                    <slot name="filters"></slot>
                  </div>
                </hf-takeover-modal>
              `}
          ${this.listLinkText
            ? html`<hf-link
                class="search-hero__list-link"
                href="${this.listLink}"
                white
              >
                ${this.listLinkText}
              </hf-link>`
            : html``}
        </div>
      </hf-page-header>
    `;
  }
}

customElements.define('hf-search-hero', SearchHero);
